import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { PredictionSection } from '../components/stream/predictions';
import StreamInfo from '../components/stream/StreamInfo';
import ChatSection from '../components/stream/ChatSection';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import useWebSocketChat from '../hooks/useWebSocketChat';

const Live = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();

  // Core state
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prediction, setPrediction] = useState(null);
  const [predictionLoading, setPredictionLoading] = useState(false); // Loading state for prediction actions
  const [predictionResult, setPredictionResult] = useState(null); // Stores result after prediction ends
  const [showPredictionForm, setShowPredictionForm] = useState(false); // Controls visibility of the creation form
  // Removed unused wasMuted state

  // Chat state
  const [messageInput, setMessageInput] = useState('');
  const messagesEndRef = useRef(null);

  // Use our WebSocket chat hook
  const {
    messages,
    error: chatError,
    sendMessage: chatSendMessage,
    isConnected: chatConnected,
    socketRef: chatSocketRef,
    isUserTyping,
    setIsUserTyping
  } = useWebSocketChat(id, user);

  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  // Stream interaction state
  const [streamState, setStreamState] = useState({
    liked: false,
    isFollowing: false,
    likeCount: 0,
    followersCount: 0,
    viewerCount: 0,
    isStreamCreator: false,
    likeLoading: false,
    followLoading: false,
    endingStream: false,
    playbackError: null
  });

  // Refs
  const twitchPlayerRef = useRef(null); // For Twitch embed
  const streamCheckInterval = useRef(null);
  const currentStreamId = useRef(id);

  // Debug helper
  const logStreamInfo = useCallback((prefix, data) => {
    console.log(`${prefix}:`, {
      id: currentStreamId.current,
      status: data?.status,
      streamUrl: data?.streamUrl,
      isLive: data?.isLive,
    });
  }, []);

  // Fetch stream
  const fetchStream = useCallback(async () => {
    if (!currentStreamId.current?.match(/^[0-9a-fA-F]{24}$/)) {
      setError('Invalid stream ID');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.streams.details(currentStreamId.current)}`, {
        headers: user?.isConnected ? {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        } : {}
      });

      if (!response.ok) {
        throw new Error('Stream not found');
      }

      const data = await response.json();
      logStreamInfo('Fetched stream', data);

      // Only set stream if it's still the same ID
      if (currentStreamId.current === id) {
        const isFollowing = data.followers?.includes(user?.address) || false;

        // **** Refined Check ****
        // Explicitly check if user and user.address exist before comparing
        let isCreator = false;
        if (user && user.address) {
            // Convert both addresses to lowercase for case-insensitive comparison
            const creatorAddress = data.creator?.address?.toLowerCase();
            const userAddress = user.address.toLowerCase();
            isCreator = creatorAddress === userAddress;

            // Enhanced logging with lowercase comparison
            console.log('[Live.jsx] Creator Check:', {
                apiCreatorAddress: data.creator?.address,
                userAddress: user.address,
                apiCreatorAddressLower: creatorAddress,
                userAddressLower: userAddress,
                isCreatorResult: isCreator
            });
        } else {
             console.log('[Live.jsx] Creator Check skipped: User or user.address missing.', { userExists: !!user, addressExists: !!user?.address });
        }
        // **** End Refined Check ****

        setStream(data);
        setStreamState(prev => ({
          ...prev,
          // Use the calculated isCreator value
          isStreamCreator: isCreator,
          liked: data.likes?.includes(user?.address) || false,
          likeCount: data.likes?.length || 0,
          isFollowing,
          followersCount: data.followers?.length || 0,
          viewerCount: data.viewers || 0
        }));

        if (data.status === 'scheduled') {
          const scheduleDate = new Date(data.scheduledStartTime);
          toast(`Stream scheduled for ${scheduleDate.toLocaleDateString()} at ${scheduleDate.toLocaleTimeString()}`);
        }
      }

    } catch (err) {
      console.error('Error fetching stream:', err);
      setError(err.message);
      toast.error(err.message);

      // Retry logic for recoverable errors
      if (retryCount < maxRetries && err.message !== 'Invalid stream ID') {
        setRetryCount(prev => prev + 1);
        setTimeout(fetchStream, 2000 * (retryCount + 1));
      }
    } finally {
      setLoading(false);
    }
  }, [id, user, retryCount, logStreamInfo]);

  // We no longer need the setupHlsPlayer function since we only support Twitch streams

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (streamCheckInterval.current) {
        clearInterval(streamCheckInterval.current);
      }
      if (window.typingTimeoutRef) {
        clearTimeout(window.typingTimeoutRef);
        window.typingTimeoutRef = null;
      }
      currentStreamId.current = null;
    };
  }, []);

  // Function to fetch predictions for the current stream
  const fetchPredictions = useCallback(async () => {
    if (!stream?.streamId) return;

    // Don't set loading state if we already have a prediction
    // This prevents flickering when refreshing predictions
    if (!prediction) {
      setPredictionLoading(true);
    }

    // Only log in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log(`Fetching predictions for stream ${stream.streamId}`);
    }

    try {
      // Try the direct route - this is the most reliable
      const response = await fetch(`${API_BASE_URL}/api/simple-predictions/${stream.streamId}`);

      if (response.ok) {
        // Success - we have a prediction
        const data = await response.json();
        // Only log in development mode
        if (process.env.NODE_ENV === 'development') {
          console.log('Prediction data received');
        }
        setPrediction(data);
      } else if (response.status === 404) {
        // 404 is expected when there are no predictions
        // This is normal for new streams or after predictions are cleared
        setPrediction(null);

        // Only log in development mode
        if (process.env.NODE_ENV === 'development') {
          console.log('No active predictions found for this stream - this is normal');
        }
      } else {
        // Other error status codes should be logged
        console.warn(`Unexpected status code ${response.status} when fetching predictions`);
        // Don't clear existing prediction on unexpected errors
        // This prevents flickering when there's a temporary API issue

        try {
          const errorData = await response.json();
          console.log('Error details:', errorData);
        } catch (e) {
          // Ignore JSON parsing errors
        }
      }
    } catch (error) {
      console.error('Error fetching predictions:', error);
      // Don't clear existing prediction on error
      // This prevents flickering when there's a temporary network issue
    } finally {
      setPredictionLoading(false);
    }
  }, [stream?.streamId, prediction]);

  // Initial stream fetch and ID update
  useEffect(() => {
    setLoading(true);
    setRetryCount(0);
    currentStreamId.current = id;
    fetchStream();
  }, [id, fetchStream]);

  // No special setup needed for Twitch streams
  // The iframe is created in the render function

  // Regular status check for live streams
  useEffect(() => {
    if (stream?.status === 'live') {
      streamCheckInterval.current = setInterval(fetchStream, 30000);
      return () => clearInterval(streamCheckInterval.current);
    }
  }, [stream?.status, fetchStream]);

  // Fetch predictions when stream data is loaded
  useEffect(() => {
    if (stream) {
      // Initial fetch - only if the form is not open
      if (!showPredictionForm) {
        fetchPredictions();
      }

      // Set up interval to periodically check for new predictions
      // Use a longer interval (60 seconds) to reduce server load
      // This is fine since we also get updates via WebSocket
      const predictionInterval = setInterval(() => {
        // Only fetch if the stream is still live, we don't already have a prediction,
        // and the prediction form is not open
        if (stream.status === 'live' && !prediction && !showPredictionForm) {
          console.log('Periodic prediction check (60s interval)');
          fetchPredictions();
        } else if (showPredictionForm) {
          console.log('Skipping periodic prediction check while form is open');
        }
      }, 60000);

      return () => clearInterval(predictionInterval);
    }
  }, [stream, fetchPredictions, prediction, showPredictionForm]);

  // WebSocket connection setup for predictions
  useEffect(() => {
    // Listen for prediction-related messages from the chat WebSocket
    const handlePredictionMessages = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'prediction_update') {
          console.log('Received prediction update:', data.prediction);

          // Don't update prediction state if the form is currently being shown
          // This prevents the form from being reset while the user is filling it out
          if (showPredictionForm) {
            console.log('Ignoring prediction update while form is open');
            return;
          }

          if (data.prediction) {
            if (data.prediction.status === 'active') {
              setPrediction(data.prediction);
              setPredictionResult(null);
            } else if (data.prediction.status === 'ended') {
              setPredictionResult(data.prediction);
              setPrediction(null);
            }
          } else {
            setPrediction(null);
            setPredictionResult(null);
          }
        } else if (data.type === 'prediction_resolved') {
          console.log('Received prediction resolved:', data.prediction);

          // Don't update prediction state if the form is currently being shown
          if (showPredictionForm) {
            console.log('Ignoring prediction resolved while form is open');
            return;
          }

          setPredictionResult(data.prediction);
          setPrediction(null);
        } else if (data.type === 'prediction_ended') {
          console.log('Received prediction_ended notification');

          // Don't update prediction state if the form is currently being shown
          if (showPredictionForm) {
            console.log('Ignoring prediction_ended while form is open');
            return;
          }

          setPrediction(null);
        } else if (data.type === 'prediction_deleted') {
          console.log('Received prediction_deleted notification');

          // Don't update prediction state if the form is currently being shown
          if (showPredictionForm) {
            console.log('Ignoring prediction_deleted while form is open');
            return;
          }

          setPrediction(null);
          setPredictionResult(null);

          if (streamState.isStreamCreator && !showPredictionForm) {
            setShowPredictionForm(true);
          }
        } else if (data.type === 'prediction_cleared') {
          console.log('Received prediction_cleared notification:', data);

          // Don't update prediction state if the form is currently being shown
          if (showPredictionForm) {
            console.log('Ignoring prediction_cleared while form is open');
            return;
          }

          setPredictionResult(null);

          if (data.isManualClear) {
            if (!streamState.isStreamCreator) {
              toast.info('The stream creator has cleared the prediction');
            }

            if (streamState.isStreamCreator && !showPredictionForm) {
              setShowPredictionForm(true);
            }
          } else {
            if (streamState.isStreamCreator) {
              toast.success('Prediction cleared. You can now create a new prediction!');
            }
          }
        } else if (data.type === 'viewer_count') {
          setStreamState(prev => ({ ...prev, viewerCount: data.count }));
        }
      } catch (err) {
        console.error('Error processing prediction WebSocket message:', err);
      }
    };

    // Add event listener to the WebSocket
    if (chatSocketRef?.current) {
      chatSocketRef.current.addEventListener('message', handlePredictionMessages);
    }

    // Also set up polling as a fallback, but don't poll when the form is open
    const predictionPollInterval = setInterval(() => {
      // Only poll if the stream exists AND the prediction form is NOT being shown
      if (stream?.streamId && !showPredictionForm) {
        console.log('Polling for predictions (form closed)');
        fetchPredictions();
      } else if (showPredictionForm) {
        console.log('Skipping prediction poll while form is open');
      }
    }, 10000); // Poll every 10 seconds as a fallback

    return () => {
      // Clean up event listeners
      if (chatSocketRef?.current) {
        chatSocketRef.current.removeEventListener('message', handlePredictionMessages);
      }
      clearInterval(predictionPollInterval);
    };
  }, [chatSocketRef, stream?.streamId, streamState.isStreamCreator, showPredictionForm, fetchPredictions]);



  // Chat message handler
  const handleSendMessage = useCallback((e) => {
    if (e.type === 'click' || (e.type === 'keypress' && e.key === 'Enter')) {
      e.preventDefault();

      if (!user?.isConnected) {
        toast.error('Please connect your wallet to chat');
        return;
      }

      if (!messageInput.trim()) return;

      try {
        // Mark that the user is done typing
        setIsUserTyping(false);

        // Use our chatSendMessage function from the useWebSocketChat hook
        const messageContent = messageInput.trim();
        const success = chatSendMessage(messageContent);

        if (success) {
          // Clear the input field
          setMessageInput('');
          console.log('Message sent successfully');
        }
      } catch (err) {
        console.error('Error sending message:', err);
        toast.error('Failed to send message. Please try again.');
      }
    }
  }, [messageInput, user, chatSendMessage, setIsUserTyping]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Log prediction state changes
  useEffect(() => {
    console.log('[Live.jsx] Prediction State Update:', {
      prediction,
      predictionResult,
      isStreamCreator: streamState.isStreamCreator,
      user: user?.address
    });

    // Note: We've removed the automatic timer for clearing predictions
    // Now we're relying on the stream creator to manually clear predictions
    // using the "Clear Prediction & Create New" button
  }, [prediction, predictionResult, streamState.isStreamCreator, user?.address]);

  // --- Prediction Handlers ---

  // Create a prediction
  const handleCreatePrediction = async (formData) => {
    if (!user?.token) {
      toast.error("Authentication required to create prediction.");
      return false; // Return false to indicate failure
    }
    if (!stream?.streamId) {
      toast.error("Stream ID not found.");
      return false; // Return false to indicate failure
    }

    console.log('Creating prediction with data:', formData);
    setPredictionLoading(true);

    // Debug the incoming formData
    console.log('[Debug] formData received in handleCreatePrediction:', {
      formData,
      incentiveType: typeof formData.incentiveType,
      incentiveAmount: typeof formData.incentiveAmount,
      incentiveAmountValue: formData.incentiveAmount
    });

    // Validate incentive amount if provided
    let validatedIncentiveAmount;
    if (formData.incentiveType && formData.incentiveAmount !== undefined && formData.incentiveAmount !== null) {
      // Ensure incentiveAmount is a valid number
      if (typeof formData.incentiveAmount === 'string') {
        validatedIncentiveAmount = parseFloat(formData.incentiveAmount);
      } else if (typeof formData.incentiveAmount === 'number') {
        validatedIncentiveAmount = formData.incentiveAmount;
      } else {
        console.error('[Debug] Invalid incentiveAmount type:', typeof formData.incentiveAmount);
        toast.error(`Invalid amount: ${formData.incentiveAmount} is not a valid number`);
        setPredictionLoading(false);
        return false;
      }

      if (isNaN(validatedIncentiveAmount) || validatedIncentiveAmount <= 0) {
        console.error('[Debug] Invalid parsed incentiveAmount:', validatedIncentiveAmount);
        toast.error(`Invalid amount: ${formData.incentiveAmount} is not a valid number`);
        setPredictionLoading(false);
        return false;
      }
    }

    // Prepare data: convert duration from minutes to seconds
    const payload = {
      ...formData,
      duration: formData.duration * 60, // Convert minutes to seconds
      incentiveAmount: validatedIncentiveAmount || undefined, // Use validated amount
      incentiveType: formData.incentiveType || undefined, // Send undefined if empty
    };

    // Debug the payload before sending to API
    console.log('[Debug] Prediction payload:', {
      payload,
      incentiveType: payload.incentiveType,
      incentiveAmount: payload.incentiveAmount
    });

    try {
      // First, try to create a test prediction to check if the database is working
      let dbWorking = false;
      try {
        const dbCheckResponse = await fetch(`${API_BASE_URL}/api/debug/db-status`);
        if (dbCheckResponse.ok) {
          const dbStatus = await dbCheckResponse.json();
          dbWorking = dbStatus.connected;
          console.log('Database status check:', dbStatus);
          if (!dbWorking) {
            toast.error('Database connection issue detected. Please try again later.');
            return false; // Return false to indicate failure
          }
        }
      } catch (dbError) {
        console.warn('Could not check database status:', dbError);
        // Continue anyway, as this is just a precaution
      }

      // Now create the actual prediction
      const response = await fetch(`${API_BASE_URL}/api/streams/${stream.streamId}/predictions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}` // Use token from context
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to create prediction' }));
        // Check for specific error messages from backend
        if (errorData.error === 'An active prediction already exists') {
          toast.error('Cannot create: An active prediction already exists for this stream.');
        } else if (errorData.error?.includes('Insufficient')) {
           toast.error(errorData.error); // Show specific balance error
        } else {
          throw new Error(errorData.error || 'Failed to create prediction');
        }
        // Don't proceed if response not ok
        setPredictionLoading(false);
        return false; // Return false to indicate failure
      }

      const newPrediction = await response.json();
      console.log('New prediction created successfully:', newPrediction);
      setPrediction(newPrediction); // Update local state with the new prediction
      setShowPredictionForm(false); // Hide form after successful creation
      toast.success('Prediction created successfully!');

      // Manually fetch predictions after a short delay to ensure the new one is included
      setTimeout(() => {
        fetchPredictions();
      }, 1000);

      return true; // Return true to indicate success

    } catch (error) {
      console.error("Error creating prediction:", error);
      toast.error(error.message || "Failed to create prediction");
      return false; // Return false to indicate failure
    } finally {
      setPredictionLoading(false);
    }
  };


  // Vote on a prediction - returns a Promise for the PredictionSection component
  const handleVotePrediction = async (option) => {
    if (!user?.token) {
      toast.error("Authentication required to vote on prediction.");
      return Promise.reject(new Error("Authentication required"));
    }
    if (!prediction?.streamId || !prediction?._id) {
      toast.error("Prediction information is missing.");
      return Promise.reject(new Error("Prediction information missing"));
    }

    console.log(`Voting for ${option} on prediction ${prediction._id}`);
    setPredictionLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/streams/${prediction.streamId}/predictions/${prediction._id}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({ option })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to vote on prediction' }));
        throw new Error(errorData.error || 'Failed to vote on prediction');
      }

      const updatedPrediction = await response.json();
      setPrediction(updatedPrediction);
      toast.success('Vote recorded successfully!');
      return Promise.resolve(updatedPrediction); // Return a resolved promise with the updated prediction
    } catch (error) {
      console.error("Error voting on prediction:", error);
      toast.error(error.message || "Failed to vote on prediction");
      setPredictionLoading(false);
      return Promise.reject(error); // Return a rejected promise with the error
    } finally {
      setPredictionLoading(false);
    }
  };

  // End a prediction and declare a winner
  const handleEndPrediction = async (winnerOptionKey) => {
    if (!user?.token) {
      toast.error("Authentication required to end prediction.");
      return;
    }
    if (!prediction?.streamId || !prediction?._id) {
      toast.error("Prediction information is missing.");
      return;
    }
    if (!streamState.isStreamCreator) {
      toast.error("Only the stream creator can end predictions.");
      return;
    }

    console.log(`Ending prediction ${prediction._id}, winner: ${winnerOptionKey}`);
    setPredictionLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/streams/${prediction.streamId}/predictions/${prediction._id}/end`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({ winner: winnerOptionKey })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to end prediction' }));
        throw new Error(errorData.error || 'Failed to end prediction');
      }

      const result = await response.json();
      setPredictionResult(result);
      setPrediction(null); // Clear the active prediction

      // Manually send a WebSocket message to ensure all clients are notified
      if (chatSocketRef?.current?.readyState === WebSocket.OPEN) {
        chatSocketRef.current.send(JSON.stringify({
          type: 'prediction_resolved',
          streamId: prediction.streamId,
          prediction: result,
          winningOption: winnerOptionKey
        }));
        console.log('Sent prediction_resolved WebSocket message');
      }

      // Inform the user that they can create a new prediction after 1 minute
      toast.success(`Prediction ended! ${result.option1} vs ${result.option2} - Winner: ${result[winnerOptionKey]}`);
      toast.info('You can create a new prediction in 1 minute', { duration: 5000 });
    } catch (error) {
      console.error("Error ending prediction:", error);
      toast.error(error.message || "Failed to end prediction");
    } finally {
      setPredictionLoading(false);
    }
  };

  // --- End Prediction Handlers ---

  // Function to manually clear prediction result for all users
  const handleClearPredictionResult = useCallback(async () => {
    console.log('Stream creator manually clearing prediction result');

    if (!user?.token) {
      toast.error("Authentication required to clear prediction.");
      return;
    }

    if (!predictionResult?._id) {
      console.warn('No prediction result to clear');
      return;
    }

    try {
      // First clear locally
      setPredictionResult(null);

      // Show loading toast
      const loadingToast = toast.loading('Deleting prediction...');

      // Call the API to delete the prediction
      const response = await fetch(`${API_BASE_URL}/api/streams/${id}/predictions/${predictionResult._id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        }
      });

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to delete prediction' }));
        throw new Error(errorData.error || 'Failed to delete prediction');
      }

      // Show success message
      toast.success('Prediction deleted. You can now create a new prediction!');

      // Show the prediction form after a short delay
      setTimeout(() => {
        setShowPredictionForm(true);
      }, 500);

      // Also send a WebSocket message to ensure all clients are notified immediately
      if (chatSocketRef?.current?.readyState === WebSocket.OPEN) {
        chatSocketRef.current.send(JSON.stringify({
          type: 'prediction_deleted',
          streamId: id,
          predictionId: predictionResult._id,
          timestamp: new Date().toISOString()
        }));
      }
    } catch (error) {
      console.error('Error deleting prediction:', error);
      toast.error(error.message || 'Failed to delete prediction');

      // Restore the prediction result if deletion failed
      setPredictionResult(predictionResult);
    }
  }, [id, user, predictionResult]);

  // Handle ending a stream
  const handleEndStream = async () => {
    if (!user?.token) {
      toast.error("Authentication required to end stream.");
      return;
    }
    if (!stream?.streamId) {
      toast.error("Stream information is missing.");
      return;
    }
    if (!streamState.isStreamCreator) {
      toast.error("Only the stream creator can end the stream.");
      return;
    }

    // Set loading state
    setStreamState(prev => ({ ...prev, endingStream: true }));

    try {
      console.log(`Ending stream ${stream.streamId}`);

      const response = await fetch(`${API_BASE_URL}/api/streams/end`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          streamId: stream.streamId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to end stream' }));
        throw new Error(errorData.error || 'Failed to end stream');
      }

      // Parse response but we don't need to use it
      await response.json();

      // Notify via WebSocket that the stream has ended
      if (chatSocketRef?.current?.readyState === WebSocket.OPEN) {
        chatSocketRef.current.send(JSON.stringify({
          type: 'stream_ended',
          streamId: stream.streamId
        }));
        console.log('Sent stream_ended WebSocket message');
      }

      // Update the stream state
      setStream(prev => ({ ...prev, status: 'ended', isLive: false }));

      toast.success('Stream ended successfully!');

      // Redirect to home page after a short delay
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (error) {
      console.error("Error ending stream:", error);
      toast.error(error.message || "Failed to end stream");
    } finally {
      setStreamState(prev => ({ ...prev, endingStream: false }));
    }
  };

  // No need to clear messages on unmount as they're managed by the useChatFallback hook

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 border-t-2 border-b-2 border-purple-500 rounded-full"
        />
        <div className="ml-4 text-white">Loading stream...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-4 bg-red-500/10 p-6 rounded-lg border border-red-500"
        >
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
            <span className="text-2xl text-red-500">!</span>
          </div>
          <h2 className="text-xl font-bold text-white">Stream Error</h2>
          <p className="text-red-500">{error}</p>
          <button
            onClick={() => {
              setLoading(true);
              setError(null);
              setRetryCount(0);
              fetchStream();
            }}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            Retry
          </button>
        </motion.div>
      </div>
    );
  }

  if (!stream) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center space-y-4">
          <h2 className="text-xl font-bold text-white">Stream Not Found</h2>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-4 p-4 text-white">
      <div className="col-span-12 lg:col-span-9 space-y-4">

        {/* Player Area */}
        <div className="bg-gray-900 rounded-lg overflow-hidden relative aspect-video min-h-[480px]">
          {stream.streamUrl ? (
            <iframe
              ref={twitchPlayerRef}
              src={`https://player.twitch.tv/?channel=${stream.streamUrl.split('/').pop()}&parent=${window.location.hostname}&muted=true`} // Extract channel, add parent domain, start muted
              height="100%"
              width="100%"
              allowFullScreen={true}
              className="absolute top-0 left-0 w-full h-full border-0"
              title="Twitch Player"
            ></iframe>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-gray-500">
              No Twitch stream URL provided
            </div>
          )}
        </div>

        {/* Stream Info */}
        <StreamInfo
          stream={stream}
          user={user}
          handleShare={() => {
            navigator.clipboard.writeText(window.location.href);
            toast.success('Stream URL copied to clipboard!');
          }}
          handleEndStream={handleEndStream}
          {...streamState}
        />

        {/* Stream Creator Controls - Direct access to clear predictions */}
        {streamState.isStreamCreator && predictionResult && (
          <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-white">Stream Creator Controls</h3>
            </div>
            <button
              onClick={handleClearPredictionResult}
              className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
              Clear Prediction & Create New
            </button>
          </div>
        )}

        {/* Prediction Section */}
        {stream && (
          <PredictionSection
            // Data props
            prediction={prediction} // Pass the current prediction state
            predictionLoading={predictionLoading}
            predictionResult={predictionResult}
            streamId={stream.streamId}
            isStreamCreator={streamState.isStreamCreator}
            user={user}
            // State control props
            showPredictionForm={showPredictionForm}
            onShowForm={() => setShowPredictionForm(true)}
            onHideForm={() => setShowPredictionForm(false)}
            // Action handlers
            onCreatePrediction={handleCreatePrediction}
            onVotePrediction={handleVotePrediction}
            onEndPrediction={handleEndPrediction}
            onClearPredictionResult={handleClearPredictionResult}
            // socketRef={socketRef} // Pass socketRef ONLY if PredictionSection directly uses it
          />
        )}
      </div>

      {/* Chat Section */}
      <ChatSection
        messages={messages}
        chatError={chatError}
        messageInput={messageInput}
        setMessageInput={(value) => {
          // Update the message input
          setMessageInput(value);

          // Mark that the user is typing when they change the input
          if (value.trim() !== messageInput.trim()) {
            setIsUserTyping(true);

            // Automatically mark as not typing after a delay
            // Use a ref instead of window to store the timeout ID
            if (window.typingTimeoutRef) {
              clearTimeout(window.typingTimeoutRef);
            }
            window.typingTimeoutRef = setTimeout(() => {
              setIsUserTyping(false);
            }, 2000);
          }
        }}
        handleSendMessage={handleSendMessage}
        messagesEndRef={messagesEndRef}
        user={user}
        streamId={id}
        isConnected={chatConnected} // Use WebSocket connection status
        socketRef={chatSocketRef}
        isUserTyping={isUserTyping}
      />
    </div>
  );
};

export default Live;
